// Where this browser's visitor came from, as tools/visit-origin.js saved it on
// the page they landed on: the first and the latest visit that came from
// another site or a tagged link ({ source, medium, campaign, referrer,
// landing, at }). The checkout saves it on the order or quote with how they
// heard about us (Data/Functions/HowDidYouHear.js), so office sees "chatgpt.com
// → /languages/albanian-to-english-translation" next to what they answered.
// A Google Ads visit also has `click`: { gclid, gbraid, wbraid }.

const cleanClick = (c) => {
  if (!c || typeof c !== "object") return null;
  const click = {
    gclid: String(c.gclid || "").slice(0, 200),
    gbraid: String(c.gbraid || "").slice(0, 200),
    wbraid: String(c.wbraid || "").slice(0, 200),
  };
  return click.gclid || click.gbraid || click.wbraid ? click : null;
};

const clean = (t) => {
  if (!(t && typeof t === "object" && t.source)) return null;
  const touch = {
    source: String(t.source).slice(0, 80),
    medium: String(t.medium || "").slice(0, 40),
    campaign: String(t.campaign || "").slice(0, 80),
    referrer: String(t.referrer || "").slice(0, 80),
    landing: String(t.landing || "").slice(0, 200),
    at: Number(t.at) || 0,
  };
  const click = cleanClick(t.click);
  return click ? { ...touch, click } : touch;
};

// The Google Ads click an origin carries: the latest visit's, else the first's.
// { gclid, gbraid, wbraid, at } or null.
export const originAdClick = (origin) => {
  const touch = [origin && origin.last, origin && origin.first].map(clean).find((t) => t && t.click);
  return touch ? { ...touch.click, at: touch.at } : null;
};

// What office saves on a quote it makes for someone who wrote on WhatsApp with
// a reference (tools/visit-origin.js saved the click under it): the same shape
// the checkout saves, so the order made from the quote carries it too.
export const originFromAdRef = (saved) => {
  const c = saved && saved.adClick;
  const click = cleanClick(c);
  if (!click) return null;
  const touch = clean({ source: "google ads", medium: "whatsapp", campaign: c.campaign, landing: c.landing, at: Number(c.at) || 0, click });
  return { first: touch, last: touch, adRef: String(saved.adRef || "") };
};

// { first, last } or null when this browser never came from anywhere we can tell.
export const storedVisitOrigin = () => {
  try {
    const o = JSON.parse(window.localStorage.getItem("evOrigin"));
    const first = clean(o && o.first);
    const last = clean(o && o.last);
    const adRef = window.localStorage.getItem("evAdRef");
    return first || last ? { first: first || last, last: last || first, ...(adRef ? { adRef } : {}) } : null;
  } catch (e) {
    return null;
  }
};

const day = (at) => (at ? new Date(at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");
const touchLabel = (t) => [
  t.medium ? `${t.source} (${t.medium})` : t.source,
  t.landing ? `→ ${t.landing}` : "",
  day(t.at) ? `· ${day(t.at)}` : "",
].filter(Boolean).join(" ");

// Office: "chatgpt.com → /languages/polish-to-english-translation · Oct 19",
// and the first visit too when it came from somewhere else.
export const visitOriginLabel = (origin) => {
  const first = clean(origin && origin.first);
  const last = clean(origin && origin.last);
  if (!last && !first) return "";
  const latest = last || first;
  const same = first && last && first.source === last.source && first.landing === last.landing;
  return same || !first ? touchLabel(latest) : `${touchLabel(latest)} (first: ${touchLabel(first)})`;
};
